/**
 * SERVICE WORKER REGISTRATION
 * ============================
 * Utilitário para registrar e gerenciar o Service Worker
 */

import * as React from 'react';

export interface SwConfig {
  onSuccess?: (registration: ServiceWorkerRegistration) => void;
  onUpdate?: (registration: ServiceWorkerRegistration) => void;
  onOffline?: () => void;
  onOnline?: () => void;
}

/**
 * Registra o Service Worker
 */
export function registerServiceWorker(config?: SwConfig) {
  // Só funciona em produção e se o navegador suporta
  if ('serviceWorker' in navigator) {
    window.addEventListener('load', () => {
      navigator.serviceWorker
        .register('/sw.js')
        .then(registration => {
          console.log('✅ Service Worker registered:', registration.scope);

          // Verifica por atualizações a cada hora
          setInterval(
            () => {
              void registration.update();
            },
            60 * 60 * 1000
          );

          registration.onupdatefound = () => {
            const installingWorker = registration.installing;

            if (installingWorker) {
              installingWorker.onstatechange = () => {
                if (installingWorker.state === 'installed') {
                  if (navigator.serviceWorker.controller) {
                    // Nova versão disponível
                    console.log('🔄 New content is available; please refresh.');

                    if (config && config.onUpdate) {
                      config.onUpdate(registration);
                    }
                  } else {
                    // Conteúdo cacheado pela primeira vez
                    console.log('✅ Content is cached for offline use.');

                    if (config && config.onSuccess) {
                      config.onSuccess(registration);
                    }
                  }
                }
              };
            }
          };
        })
        .catch(error => {
          console.error('❌ Service Worker registration failed:', error);
        });
    });
  }

  // Detecta quando fica offline/online
  window.addEventListener('offline', () => {
    console.log('📡 App is offline');
    if (config && config.onOffline) {
      config.onOffline();
    }
  });

  window.addEventListener('online', () => {
    console.log('📡 App is online');
    if (config && config.onOnline) {
      config.onOnline();
    }
  });
}

/**
 * Desregistra o Service Worker
 */
export function unregisterServiceWorker() {
  if ('serviceWorker' in navigator) {
    navigator.serviceWorker.ready
      .then(registration => {
        registration.unregister();
      })
      .catch(error => {
        console.error(error.message);
      });
  }
}

/**
 * Atualiza o Service Worker manualmente
 */
export async function updateServiceWorker() {
  if ('serviceWorker' in navigator) {
    const registration = await navigator.serviceWorker.ready;

    // Pergunta ao SW para se atualizar
    void registration.update();

    // Envia mensagem para o SW pular espera
    if (registration.waiting) {
      registration.waiting.postMessage({ type: 'SKIP_WAITING' });
    }
  }
}

/**
 * Hook React para status do Service Worker
 */
export function useServiceWorker() {
  const [isOnline, setIsOnline] = React.useState(navigator.onLine);
  const [isInstalled, setIsInstalled] = React.useState(false);
  const [hasUpdate, setHasUpdate] = React.useState(false);
  const [registration, setRegistration] = React.useState<ServiceWorkerRegistration | null>(null);

  React.useEffect(() => {
    // Registra o SW
    registerServiceWorker({
      onSuccess: reg => {
        setIsInstalled(true);
        setRegistration(reg);
      },
      onUpdate: reg => {
        setHasUpdate(true);
        setRegistration(reg);
      },
      onOffline: () => setIsOnline(false),
      onOnline: () => setIsOnline(true),
    });

    // Listeners de online/offline
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  const reload = () => {
    if (registration && registration.waiting) {
      registration.waiting.postMessage({ type: 'SKIP_WAITING' });
      window.location.reload();
    }
  };

  return {
    isOnline,
    isInstalled,
    hasUpdate,
    registration,
    reload,
  };
}
